// The one shared placeholder for a sidebar section that is not built yet (design guide, principle 6:
// "honest empty & unbuilt states"). Every `Soon` item in the venue and organisation sidebars routes
// here — never a dead link or a blank page — and it says, for that section: the icon, the name, one
// sentence, and the sub-areas it will hold. When a section's real screen lands, its `Soon` item
// becomes a live route (`layouts.tsx`) and stops arriving here.

import { useTranslation } from '@clavian/i18n/react';
import { Chip } from '@clavian/ui-web';
import { useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { NavIcon, RoadmapIcon } from './nav-icons';

export function ComingSoonPage(): ReactNode {
  const { t } = useTranslation('console');
  // Shared by the venue and organisation layouts, so read the parameter without naming one route.
  const params = useParams({ strict: false }) as { readonly section?: string };
  const section = params.section ?? '';

  const name = t(`shell.nav.${section}`, { defaultValue: t('soon.fallbackTitle') });
  const blurb = t(`soon.${section}.blurb`, { defaultValue: t('soon.fallbackBlurb') });
  const rawAreas = t(`soon.${section}.areas`, { returnObjects: true }) as unknown;
  const areas: readonly string[] = Array.isArray(rawAreas) ? (rawAreas as readonly string[]) : [];

  return (
    <div className="console-page">
      <section className="console-coming" aria-labelledby="console-coming-title">
        <span className="console-coming__icon" aria-hidden="true">
          <NavIcon name={section} />
        </span>
        <p className="console-eyebrow console-coming__eyebrow">{t('soon.eyebrow')}</p>
        <h1 id="console-coming-title" className="console-coming__title">
          {name}
        </h1>
        <p className="console-coming__blurb">{blurb}</p>
        {areas.length > 0 ? (
          <div className="console-coming__areas">
            <p className="console-coming__areas-label">{t('soon.areasLabel')}</p>
            <ul className="console-coming__chips">
              {areas.map((area) => (
                <li key={area}>
                  <Chip>{area}</Chip>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="console-coming__roadmap">
          <span className="console-coming__roadmap-icon" aria-hidden="true">
            <RoadmapIcon />
          </span>
          {t('soon.roadmap')}
        </p>
      </section>
    </div>
  );
}
